import { shell } from '../../app';
import { copyText } from '../../data/drafts';
import type { BriefDraft } from '../../logic/brief';
import { btn } from '../../ui/components';
import { modal } from '../../ui/dialog';
import { h } from '../../ui/dom';

/** The draft behind a brief item (D89): the library message rendered for this merchant, ready to
 *  copy. The app never sends it — Artur sends from his own machine (project guardrail), so every
 *  action here puts text on the clipboard and nothing else. */

/** A [[PLACEHOLDER]] only Artur can fill. A draft carrying one is not ready to send, and the sheet
 *  says so rather than letting it go out with the brackets in it. */
export const unfilled = (text: string): string[] => [
  ...new Set([...text.matchAll(/\[\[([A-Z0-9_]+)\]\]/g)].map((m) => m[1] as string)),
];

const copyAction = (label: string, text: string) =>
  btn(label, {
    variant: 'subtle',
    size: 'sm',
    onClick: () =>
      void copyText(text).then((ok) =>
        shell.toast(ok ? `${label.replace('Copy ', '')} copied` : 'Could not copy', {
          kind: ok ? 'success' : 'error',
        }),
      ),
  });

export const draftSheet = (name: string, draft: BriefDraft, alternates: readonly BriefDraft[]): void => {
  const body = (d: BriefDraft) => {
    const gaps = unfilled([d.subject ?? '', d.body].join('\n'));
    const whole = d.subject ? `${d.subject}\n\n${d.body}` : d.body;
    return h(
      'article',
      { class: 'draft' },
      h('h3', { class: 'draft__title' }, d.message.title, h('span', { class: 'draft__code mono' }, d.message.code)),
      d.message.when_to_use ? h('p', { class: 'draft__when' }, d.message.when_to_use) : null,
      d.subject === null
        ? h('p', { class: 'draft__nosubject' }, 'No subject line — the library leaves this one to you.')
        : h('div', { class: 'draft__subject' }, d.subject),
      h('pre', { class: 'draft__body' }, d.body),
      gaps.length
        ? h(
            'p',
            { class: 'draft__gaps' },
            `Fill before sending: ${gaps.join(', ')}. A draft with one of these left in it is not ready.`,
          )
        : null,
      h(
        'div',
        { class: 'draft__actions' },
        d.subject ? copyAction('Copy subject', d.subject) : null,
        copyAction('Copy body', d.body),
        d.subject ? copyAction('Copy both', whole) : null,
      ),
    );
  };

  const sheet = modal(
    name,
    body(draft),
    alternates.length
      ? h(
          'div',
          { class: 'draft__alts' },
          h('h4', { class: 'draft__altstitle mono' }, 'Also worth having to hand'),
          alternates.map(body),
        )
      : null,
  );
  sheet.el.addEventListener('close', () => sheet.remove());
  sheet.open();
};
